<script>
  /**
   * NavigationGuard — confirms before an in-app link click leaves the page.
   *
   * Renders nothing. While `when` is true it holds a capture-phase click
   * listener on `document`, so it sees anchor clicks before svelte-navigator's
   * `Link` and `use:link` handlers, which are attached to the anchors
   * themselves. Declining the prompt cancels the click.
   *
   * Best-effort by design: it covers in-app link clicks, not the browser back
   * button or closing the tab.
   *
   * @example
   * <NavigationGuard when={phase === 'deleting'} message={$format('label.X')} />
   */

  import { onDestroy } from 'svelte';

  /**
   * Whether the guard is armed.
   * @type {boolean}
   */
  export let when = false;

  /**
   * Already-formatted prompt text, passed straight to `confirm()`.
   * @type {string}
   */
  export let message = '';

  let attached = false;

  /**
   * Only clicks that would actually unmount the page qualify. Modified and
   * non-primary clicks open a new tab, leaving this page running.
   */
  const isLeavingClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) {
      return false;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return false;
    }

    const anchor = event.target?.closest?.('a[href]');

    return Boolean(anchor) && (!anchor.target || anchor.target === '_self');
  };

  const onClick = (event) => {
    if (!isLeavingClick(event) || window.confirm(message)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const attach = () => {
    if (attached) {
      return;
    }

    document.addEventListener('click', onClick, true);
    attached = true;
  };

  const detach = () => {
    if (!attached) {
      return;
    }

    document.removeEventListener('click', onClick, true);
    attached = false;
  };

  $: if (when) {
    attach();
  } else {
    detach();
  }

  onDestroy(detach);
</script>
